'use client';

import { useEffect, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import { PostSummary } from '@/lib/mdx';
import { isGeneratedImage } from '@/lib/post-image';

import { BookOpen } from 'lucide-react';

/** Responsive stand-in for posts without artwork: text reflows instead of being cropped like a picture. */
function GeneratedCover({
  title,
  category
}: {
  title: string;
  category?: string;
}) {
  return (
    <div className='flex h-full w-full flex-col justify-between bg-gradient-to-br from-[#0a2e22] via-[#0E3D2D] to-[#12513b] p-4 text-white sm:p-5'>
      <span className='text-[10px] font-bold tracking-[0.2em] text-[#9ae8c9] uppercase'>
        {category}
      </span>
      <span className='line-clamp-5 font-serif text-base leading-snug font-bold sm:text-lg'>
        {title}
      </span>
      <span className='text-[10px] font-semibold text-[#9ae8c9]'>
        Bioinfometrics
      </span>
    </div>
  );
}

interface PostCardProps {
  post: PostSummary;
  featured?: boolean;
}

export function PostCard({ post, featured = false }: PostCardProps) {
  const {
    title,
    description,
    date,
    author,
    readingTime,
    image,
    tags,
    categories
  } = post.meta;
  const slug = post.slug;
  const generated = isGeneratedImage(image?.src);
  const category = categories?.[0];
  const tagsString = tags?.length
    ? tags.slice(0, 3).join(', ')
    : categories?.length
      ? categories.join(', ')
      : 'Bioinfometrics, Article';

  // Determine if we need the /blog prefix based on the current hostname
  // On the blog subdomain, we want clean URLs without /blog
  const [isBlogSubdomain, setIsBlogSubdomain] = useState(false);

  useEffect(() => {
    const host = window.location.hostname;
    if (
      host.includes('blog.bioinfometrics.com') ||
      host.includes('blog.localhost')
    ) {
      setIsBlogSubdomain(true);
    }
  }, []);

  const blogPath = isBlogSubdomain ? '' : '/blog';

  if (featured) {
    return (
      <div className='animate-fade-in-up border-border/10 mb-16 border-b pb-16 [animation-duration:800ms]'>
        <Link
          href={`${blogPath}/${slug}`}
          className='group relative block overflow-hidden transition-all duration-300 hover:-translate-y-1'>
          <div className='grid h-full gap-8 md:grid-cols-2'>
            <div className='flex flex-col justify-center py-4 md:py-8'>
              <div className='text-muted-foreground mb-6 flex items-center gap-4 font-mono text-xs tracking-widest uppercase'>
                <svg
                  className='size-3'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'>
                  <path d='M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' />
                </svg>
                <span>{date}</span>
                <span className='bg-border size-1 rounded-full' />
                <svg
                  className='size-3'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'>
                  <circle
                    cx='12'
                    cy='12'
                    r='10'
                  />
                  <path d='M12 6v6l4 2' />
                </svg>
                <span>{readingTime}</span>
              </div>
              <h2 className='text-foreground group-hover:text-primary mb-6 font-sans text-4xl leading-tight font-bold tracking-tight transition-colors md:text-5xl'>
                {title}
              </h2>
              <p className='text-muted-foreground mb-12 line-clamp-3 text-lg leading-relaxed'>
                {description}
              </p>
              <div className='mt-auto flex flex-col gap-2 font-mono text-xs'>
                <div className='text-muted-foreground flex items-center gap-2'>
                  <div className='border-border/20 group-hover:border-primary/50 mr-2 rounded-full border p-2 transition-colors'>
                    <svg
                      className='size-4'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'>
                      <path d='M5 12h14M12 5l7 7-7 7' />
                    </svg>
                  </div>
                  <span>Author/</span>
                  <span className='text-foreground font-semibold'>
                    {author}
                  </span>
                </div>
                <div className='text-muted-foreground flex items-center gap-2'>
                  <span>Tags/</span>
                  <span className='text-primary line-clamp-1 font-semibold'>
                    {tagsString}
                  </span>
                </div>
              </div>
            </div>
            <div className='relative flex h-[400px] w-full items-center justify-center overflow-hidden md:h-full'>
              <div className='from-primary/10 via-background to-background absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))]' />
              {/* Radial lines mimicking Hex */}
              <div
                className='absolute inset-0 opacity-20'
                style={{
                  backgroundImage:
                    'repeating-linear-gradient(45deg, transparent, transparent 10px, var(--primary) 10px, var(--primary) 11px)'
                }}></div>
              <div className='relative h-64 w-64 overflow-hidden rounded-sm shadow-2xl ring-1 ring-white/10 md:h-80 md:w-80'>
                {generated ? (
                  <GeneratedCover
                    title={title}
                    category={category}
                  />
                ) : image ? (
                  <Image
                    src={image.src}
                    alt={image.alt || title}
                    fill
                    priority
                    sizes='(max-width: 768px) 100vw, 50vw'
                    className='object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]'
                  />
                ) : (
                  <div className='from-primary/10 via-background to-secondary/10 flex h-full w-full items-center justify-center bg-gradient-to-br'>
                    <BookOpen className='text-primary/20 size-12' />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  }

  return (
    <div className='animate-fade-in-up h-full [animation-duration:800ms]'>
      <Link
        href={`${blogPath}/${slug}`}
        className='group flex h-full flex-col overflow-hidden transition-all duration-300 hover:-translate-y-1'>
        <div className='bg-muted relative aspect-[16/10] w-full overflow-hidden rounded-sm ring-1 ring-white/10'>
          {generated ? (
            <GeneratedCover
              title={title}
              category={category}
            />
          ) : image ? (
            <Image
              src={image.src}
              alt={image.alt || title}
              fill
              sizes='(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'
              className='object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]'
            />
          ) : (
            <div className='from-primary/5 to-secondary/5 flex h-full w-full items-center justify-center bg-gradient-to-br'>
              <BookOpen className='text-primary/10 size-8' />
            </div>
          )}
        </div>
        <div className='flex flex-1 flex-col py-6'>
          <div className='text-muted-foreground mb-4 flex items-center gap-3 font-mono text-xs tracking-widest uppercase'>
            <svg
              className='size-3'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'>
              <path d='M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' />
            </svg>
            <span>{date}</span>
            <span className='bg-border size-1 rounded-full' />
            <svg
              className='size-3'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'>
              <circle
                cx='12'
                cy='12'
                r='10'
              />
              <path d='M12 6v6l4 2' />
            </svg>
            <span>{readingTime}</span>
          </div>
          <h3 className='text-foreground group-hover:text-primary mb-3 line-clamp-2 font-sans text-2xl leading-tight font-bold tracking-tight transition-colors'>
            {title}
          </h3>
          <p className='text-muted-foreground mb-8 line-clamp-3 flex-1 text-sm leading-relaxed'>
            {description}
          </p>
          <div className='mt-auto flex flex-col gap-1 font-mono text-xs'>
            <div className='text-muted-foreground flex items-center gap-2'>
              <span>Author/</span>
              <span className='text-foreground font-semibold'>{author}</span>
            </div>
            <div className='text-muted-foreground flex items-center gap-2'>
              <span>Tags/</span>
              <span className='text-primary line-clamp-1 font-semibold'>
                {tagsString}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}
